import { useEffect, useRef, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { AlertTriangle, Ban, CheckCircle2, CreditCard, Download, Eye, FileEdit, FileText, PackageCheck, Paperclip, Pencil, Plus, Printer, Scale, ShoppingCart, Trash2, Truck, UserPlus } from 'lucide-react';
import { Button, DataTable, FormField, Modal, PageHeader, StatusBadge, Tabs } from '@/components/ui';
import { Input } from '@/components/ui/input';
import { getApiErrorMessage } from '@/lib/axios';
import { cn } from '@/lib/utils';
import {
  type BCDTO,
  type DADTO,
  type FactureDTO,
  type FactureFiltres,
  type ModePaiementFacture,
  type PaiementDTO,
  type PieceJointe,
  type RapprochementDTO,
  type ReceptionDTO,
  type StatutPaiement,
  type TiersTypeFacture,
  type TypeDocFacture,
  DA_STATUT_LABEL,
  FACTURE_STATUT_LABEL,
  FACTURE_STATUTS,
  MODES_PAIEMENT_FACTURE,
  MODE_PAIEMENT_FACTURE_LABEL,
  STATUT_PAIEMENT_LABEL,
  TIERS_TYPES_FACTURE,
  TIERS_TYPE_FACTURE_LABEL,
  TYPE_DOC_LABEL,
  TVA_TAUX_FACTURE,
  addPaiement,
  updatePaiement,
  deletePaiement,
  quotePartHtTva,
  UNITES_ACHAT,
  convertirDA,
  createBC,
  createDA,
  createFacture,
  createReception,
  deleteDA,
  deleteFacture,
  getFacturesTotaux,
  getRapprochement,
  listBC,
  listDA,
  listFactures,
  getAchatsSynthese,
  listReceptions,
  openBCPdf,
  openFacturePdf,
  openDAPdf,
  openReceptionPdf,
  refuserDA,
  updateBC,
  validerBC,
  validerDA,
  validerFacture,
  updateFacture,
} from '@/services/achats';
import { usePerms } from '@/hooks/usePerms';
import { listChantiers } from '@/services/chantiers';
import { listCentresCout } from '@/services/parametres';
import { useAuthStore } from '@/store/authStore';
import { listArticles, listDepots } from '@/services/stock';
import { createTiers, listTiers, type TiersDTO } from '@/services/tiers';
import { uploadFile } from '@/services/uploads';
import { toast } from '@/store/toastStore';
import type { StatusKey } from '@/types';
import { formatDZD, formatDZDCompact, formatDateDZ } from '@/utils/algeria';
import { type DetailDoc, DetailDocumentModal } from './DetailDocumentModal';
import { FactureDetailModal } from './FactureDetailModal';

const DA_STATUT_UI: Record<string, StatusKey> = {
  BROUILLON: 'pending', VALIDEE: 'info', CONVERTIE: 'done', REFUSEE: 'stuck',
};
const BC_STATUT_UI: Record<string, { label: string; status: StatusKey }> = {
  BROUILLON: { label: 'Brouillon', status: 'pending' },
  ENVOYE: { label: 'Envoyé', status: 'info' },
  CONFIRME: { label: 'Confirmé', status: 'working' },
  PARTIELLEMENT_LIVRE: { label: 'Partiel', status: 'working' },
  LIVRE: { label: 'Livré', status: 'done' },
  ANNULE: { label: 'Annulé', status: 'stuck' },
};

function SyntheseBanner() {
  const { data } = useQuery({ queryKey: ['achats-synthese'], queryFn: getAchatsSynthese });
  const items = [
    { label: 'Total engagé', sub: 'BC validés', value: formatDZDCompact(data?.engage_total ?? 0), icon: ShoppingCart, accent: '#1a1a2e' },
    { label: 'Total facturé', sub: 'factures validées', value: formatDZDCompact(data?.facture_total ?? 0), icon: FileText, accent: '#64748b' },
    { label: 'À réceptionner', sub: 'BC en cours', value: String(data?.a_receptionner ?? 0), icon: Truck, accent: '#0073EA' },
    { label: 'À enregistrer', sub: 'BC livrés sans facture', value: String(data?.a_enregistrer ?? 0), icon: FileEdit, accent: '#F97316' },
  ];
  return (
    <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
      {items.map((it) => (
        <div key={it.label} className="flex items-center gap-3 rounded-lg border border-border bg-surface-secondary p-4 shadow-sm">
          <span className="flex size-10 shrink-0 items-center justify-center rounded-md" style={{ backgroundColor: `${it.accent}1A`, color: it.accent }}><it.icon className="size-5" /></span>
          <div className="min-w-0">
            <p className="truncate text-lg font-bold tabular-nums text-content-primary" title={it.value}>{it.value}</p>
            <p className="truncate text-xs font-medium text-content-secondary">{it.label}</p>
            <p className="truncate text-[11px] text-content-muted">{it.sub}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

export function AchatsPage() {
  const [tab, setTab] = useState('da');
  return (
    <>
      <PageHeader
        title="Achats & approvisionnements"
        subtitle="Circuit Demande d'achat → Bon de commande → Réception → Facture"
        breadcrumbs={[{ label: 'Advanced Works ERP' }, { label: 'Achats' }]}
      />

      {/* Stepper visuel du circuit */}
      <div className="mb-6 hidden items-center gap-3 rounded-lg border border-border bg-surface-secondary px-5 py-3 text-sm shadow-sm sm:flex">
        {[
          { icon: FileEdit, label: "Demande d'achat" },
          { icon: ShoppingCart, label: 'Bon de commande' },
          { icon: PackageCheck, label: 'Réception' },
        ].map((s, i) => (
          <div key={s.label} className="flex items-center gap-3">
            <span className="flex items-center gap-2 font-medium text-content-secondary">
              <span className="flex size-7 items-center justify-center rounded-full bg-brand-primary/10 text-brand-primary">
                <s.icon className="size-4" />
              </span>
              {s.label}
            </span>
            {i < 2 && <span className="text-content-muted">→</span>}
          </div>
        ))}
      </div>

      <SyntheseBanner />

      <Tabs
        tabs={[
          { key: 'da', label: "Demandes d'achat", icon: <FileEdit className="size-4" /> },
          { key: 'bc', label: 'Bons de commande', icon: <ShoppingCart className="size-4" /> },
          { key: 'rec', label: 'Réceptions', icon: <PackageCheck className="size-4" /> },
        ]}
        value={tab}
        onChange={setTab}
        className="mb-6"
      />

      {tab === 'da' && <DemandesAchatTab onConverted={() => setTab('bc')} />}
      {tab === 'bc' && <BonsCommandeTab />}
      {tab === 'rec' && <ReceptionsTab />}
    </>
  );
}

interface DAForm {
  axe: '' | 'CHANTIER' | 'CENTRE_COUT';
  chantier_id: string;
  centre_cout_id: string;
  fournisseur_id: string;
  fournisseur_nom_libre: string;
  comptabilise: boolean;
  tva_deductible: boolean;
  tva_pct: string;
  notes: string;
  lignes: { designation: string; unite: string; quantite: number; prix_unitaire: number; article_stock_id: string }[];
}
const DA_DEFAULTS: DAForm = { axe: '', chantier_id: '', centre_cout_id: '', fournisseur_id: '', fournisseur_nom_libre: '', comptabilise: false, tva_deductible: true, tva_pct: '0.19', notes: '', lignes: [{ designation: '', unite: '', quantite: 1, prix_unitaire: 0, article_stock_id: '' }] };
const IMPUTATION_LABEL: Record<string, string> = { CHANTIER: 'Chantier', CENTRE_COUT: 'Centre de coût' };

/** Demande d'achat → structure commune de la vue détail (même contenu que le PDF). */
function daToDetail(da: DADTO): DetailDoc {
  return {
    titre: "Demande d'achat",
    reference: da.reference,
    statut: { label: DA_STATUT_LABEL[da.statut as keyof typeof DA_STATUT_LABEL], key: DA_STATUT_UI[da.statut] },
    entete: [
      { label: 'Demandeur', value: da.demandeur_nom },
      { label: 'Date', value: da.date_demande ? formatDateDZ(da.date_demande) : null },
      { label: 'Imputation', value: IMPUTATION_LABEL[da.axe_analytique ?? ''] ?? '—' },
      { label: 'Fournisseur (suggéré)', value: da.fournisseur_nom },
      { label: 'Notes', value: da.notes },
    ],
    lignes: da.lignes.map((l) => ({ designation: l.designation, unite: l.unite, quantite: l.quantite, prix_unitaire: l.prix_unitaire ?? null, montant: l.montant_ligne ?? null })),
    avecMontants: true,
    totaux: { ht: da.montant_ht, tva: da.tva_montant, ttc: da.montant_ttc, tva_pct: da.tva_pct },
  };
}

function DemandesAchatTab({ onConverted }: { onConverted: () => void }) {
  const queryClient = useQueryClient();
  const perms = usePerms('achats');
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState<DetailDoc | null>(null);
  const { data, isLoading } = useQuery({ queryKey: ['demandes-achat'], queryFn: listDA });
  const { data: chantiers } = useQuery({ queryKey: ['chantiers'], queryFn: () => listChantiers(), enabled: open });
  const { data: centres } = useQuery({ queryKey: ['centres-cout'], queryFn: listCentresCout, enabled: open });
  const { data: fournisseurs } = useQuery({ queryKey: ['tiers', 'FOURNISSEUR'], queryFn: () => listTiers({ type: 'FOURNISSEUR', size: 200 }), enabled: open });
  const { data: articles } = useQuery({ queryKey: ['articles'], queryFn: () => listArticles({ size: 500 }), enabled: open });
  const moi = useAuthStore((s) => s.user);
  const moiNom = moi ? ([moi.prenom, moi.nom].filter(Boolean).join(' ') || moi.email) : '—';
  const [q, setQ] = useState('');
  const [stf, setStf] = useState('');

  const { register, handleSubmit, control, reset, watch } = useForm<DAForm>({ defaultValues: DA_DEFAULTS });
  const { fields, append, remove } = useFieldArray({ control, name: 'lignes' });
  const axe = watch('axe');
  const comptabilise = watch('comptabilise');
  const fournisseurId = watch('fournisseur_id');
  const lignesW = watch('lignes');
  const totalHt = (lignesW ?? []).reduce((s, l) => s + (Number(l.quantite) || 0) * (Number(l.prix_unitaire) || 0), 0);
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['demandes-achat'] });

  const createMutation = useMutation({
    mutationFn: (v: DAForm) => createDA({
      notes: v.notes || null,
      axe_analytique: v.axe || null,
      chantier_id: v.axe === 'CHANTIER' ? (v.chantier_id || null) : null,
      centre_cout_id: v.axe === 'CENTRE_COUT' ? (v.centre_cout_id || null) : null,
      fournisseur_id: v.fournisseur_id || null,
      fournisseur_nom_libre: v.fournisseur_id ? null : (v.fournisseur_nom_libre || null),
      comptabilise: !!v.comptabilise,
      tva_deductible: !!v.tva_deductible,
      tva_pct: Number(v.tva_pct) || 0,
      lignes: v.lignes.filter((l) => l.designation).map((l) => ({ designation: l.designation, unite: l.unite || null, quantite: Number(l.quantite) || 0, prix_unitaire: Number(l.prix_unitaire) || 0, article_stock_id: l.article_stock_id || null })),
    }),
    onSuccess: () => { invalidate(); setOpen(false); reset(DA_DEFAULTS); toast.success("Demande d'achat créée (brouillon)."); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const validerMutation = useMutation({ mutationFn: validerDA, onSuccess: (da) => { invalidate(); toast.success(`Demande validée : ${da.reference}.`); }, onError: (e) => toast.error(getApiErrorMessage(e)) });
  const refuserMutation = useMutation({ mutationFn: ({ id, motif }: { id: string; motif: string | null }) => refuserDA(id, motif), onSuccess: () => { invalidate(); toast.success('Demande refusée.'); }, onError: (e) => toast.error(getApiErrorMessage(e)) });
  const deleteMutation = useMutation({ mutationFn: deleteDA, onSuccess: () => { invalidate(); toast.success('Demande supprimée.'); }, onError: (e) => toast.error(getApiErrorMessage(e)) });
  const convertMutation = useMutation({
    mutationFn: convertirDA,
    onSuccess: () => { invalidate(); queryClient.invalidateQueries({ queryKey: ['bons-commande'] }); toast.success('Bon de commande créé (brouillon). Onglet Bons de commande → Modifier pour renseigner le fournisseur et les prix, puis Valider.'); onConverted(); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const columns: ColumnDef<DADTO, unknown>[] = [
    { accessorKey: 'reference', header: 'Référence', size: 130, cell: ({ getValue }) => (getValue() as string) ?? <span className="text-content-muted italic">Brouillon</span> },
    { accessorKey: 'demandeur_nom', header: 'Demandeur', size: 140, cell: ({ getValue }) => (getValue() as string) ?? '—' },
    { accessorKey: 'axe_analytique', header: 'Imputation', size: 120, cell: ({ getValue }) => IMPUTATION_LABEL[getValue() as string] ?? '—' },
    { accessorKey: 'fournisseur_nom', header: 'Fournisseur', size: 150, cell: ({ getValue }) => (getValue() as string) ?? '—' },
    { accessorKey: 'montant_ttc', header: 'Montant TTC', size: 130, cell: ({ getValue }) => <span className="tabular-nums font-medium">{formatDZD(getValue() as number)}</span> },
    { accessorKey: 'statut', header: 'Statut', size: 100, cell: ({ getValue }) => { const s = getValue() as string; return <StatusBadge status={DA_STATUT_UI[s]} label={DA_STATUT_LABEL[s as keyof typeof DA_STATUT_LABEL]} />; } },
    {
      id: 'actions', header: 'Workflow', size: 280,
      cell: ({ row }) => {
        const da = row.original;
        return (
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setDetail(daToDetail(da))} aria-label="Voir le détail"><Eye className="size-4" /></Button>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openDAPdf(da.id, 'open').catch((e) => toast.error(getApiErrorMessage(e)))} aria-label="Imprimer la demande d'achat"><Printer className="size-4" /></Button>
            {da.statut === 'BROUILLON' && <Button size="sm" onClick={() => validerMutation.mutate(da.id)} loading={validerMutation.isPending}><CheckCircle2 className="size-4" /> Valider</Button>}
            {da.statut === 'VALIDEE' && <Button size="sm" onClick={() => convertMutation.mutate(da.id)} loading={convertMutation.isPending}><ShoppingCart className="size-4" /> Convertir en BC</Button>}
            {da.statut === 'VALIDEE' && <Button variant="secondary" size="sm" className="text-status-stuck" onClick={() => { const m = window.prompt('Motif du refus ?'); if (m !== null) refuserMutation.mutate({ id: da.id, motif: m || null }); }}><Ban className="size-4" /> Refuser</Button>}
            {(da.statut === 'BROUILLON' || da.statut === 'REFUSEE') && <Button variant="ghost" size="icon" className="h-8 w-8 text-status-stuck" onClick={() => { if (window.confirm('Supprimer cette demande ?')) deleteMutation.mutate(da.id); }} aria-label="Supprimer"><Trash2 className="size-4" /></Button>}
            {da.statut === 'CONVERTIE' && <span className="inline-flex items-center gap-1 text-xs font-medium text-status-done"><CheckCircle2 className="size-3.5" /> Bon de commande créé</span>}
          </div>
        );
      },
    },
  ];

  const submitCreate = handleSubmit((v) => {
    if (!v.lignes.some((l) => l.designation.trim())) { toast.error('Ajoutez au moins un article (désignation) avant de créer la demande.'); return; }
    createMutation.mutate(v);
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-content-secondary">
          Circuit : <span className="font-medium">Brouillon</span> → <span className="font-medium">Valider</span> (n° DA-AAAA-NNNN) → <span className="font-medium">Convertir en bon de commande</span>. Les actions sont dans la colonne <span className="font-medium">Workflow</span>.
        </p>
        {perms.canAjouter && <Button onClick={() => { reset(DA_DEFAULTS); setOpen(true); }}><Plus /> Nouvelle demande d'achat</Button>}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Input placeholder="Rechercher (n°, désignation…)" value={q} onChange={(e) => setQ(e.target.value)} className="h-9 max-w-xs" />
        <select value={stf} onChange={(e) => setStf(e.target.value)} className="h-9 rounded-md border border-border bg-surface-secondary px-2 text-sm">
          <option value="">Tous les statuts</option>
          {Object.entries(DA_STATUT_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>
      <DataTable columns={columns} data={(data?.items ?? []).filter((r) => !stf || r.statut === stf)} globalFilter={q} loading={isLoading} pageSize={15} exportFileName="demandes-achat" emptyMessage="Aucune demande d'achat." />
      <DetailDocumentModal open={!!detail} onClose={() => setDetail(null)} doc={detail} />

      <Modal open={open} onOpenChange={setOpen} title="Nouvelle demande d'achat" size="xl"
        footer={<><Button variant="secondary" onClick={() => setOpen(false)}>Annuler</Button><Button onClick={submitCreate} loading={createMutation.isPending}>Créer (brouillon)</Button></>}>
        {/* Demandeur (« demandé par ») + imputation analytique chantier OU centre de coût. */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField label="Demandeur (demandé par)" hint="Vous, automatiquement — non modifiable">
            <div className="flex h-10 items-center rounded-md border border-border bg-surface-primary px-3 text-sm text-content-secondary">
              {moiNom}
            </div>
          </FormField>
          <FormField label="Imputation">
            <select {...register('axe')} className={selectCls}>
              <option value="">— Non imputée —</option>
              <option value="CHANTIER">Chantier</option>
              <option value="CENTRE_COUT">Centre de coût (structure / bureau)</option>
            </select>
          </FormField>
        </div>
        {axe && (
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {axe === 'CHANTIER' && (
              <FormField label="Chantier"><select {...register('chantier_id')} className={selectCls}><option value="">— Sélectionner —</option>{chantiers?.items.map((c) => <option key={c.id} value={c.id}>{c.code} · {c.nom}</option>)}</select></FormField>
            )}
            {axe === 'CENTRE_COUT' && (
              <FormField label="Centre de coût"><select {...register('centre_cout_id')} className={selectCls}><option value="">— Sélectionner —</option>{centres?.map((c) => <option key={c.id} value={c.id}>{c.code} · {c.libelle}</option>)}</select></FormField>
            )}
          </div>
        )}
        {/* Fournisseur : connu (annuaire) ou saisi librement. */}
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField label="Fournisseur (connu)">
            <select {...register('fournisseur_id')} className={selectCls}><option value="">— Aucun / inconnu —</option>{fournisseurs?.items.map((t) => <option key={t.id} value={t.id}>{t.raison_sociale}</option>)}</select>
          </FormField>
          {!fournisseurId && <FormField label="…ou nom du fournisseur (libre)"><Input {...register('fournisseur_nom_libre')} placeholder="Fournisseur ponctuel / inconnu" /></FormField>}
        </div>
        {/* Volet comptable estimatif. */}
        <div className="mt-4 flex flex-wrap items-center gap-4 rounded-md border border-border bg-surface-primary px-4 py-3">
          <label className="flex items-center gap-2 text-sm text-content-secondary"><input type="checkbox" className="size-4 accent-brand-primary" {...register('comptabilise')} /> Achat comptabilisé</label>
          {comptabilise && <label className="flex items-center gap-2 text-sm text-content-secondary"><input type="checkbox" className="size-4 accent-brand-primary" {...register('tva_deductible')} /> TVA déductible</label>}
          {comptabilise && (
            <FormField label="Taux TVA" className="mb-0"><select {...register('tva_pct')} className={selectCls}><option value="0">0 %</option><option value="0.09">9 %</option><option value="0.19">19 %</option></select></FormField>
          )}
        </div>
        <div className="mt-4">
          <div className="mb-2 flex items-center justify-between">
            <p className="label-caps">Articles demandés</p>
            <Button variant="ghost" size="sm" onClick={() => append({ designation: '', unite: '', quantite: 1, prix_unitaire: 0, article_stock_id: '' })}><Plus className="size-4" /> Ligne</Button>
          </div>
          <div className="space-y-2">
            {fields.map((f, i) => (
              <div key={f.id} className="grid grid-cols-12 gap-2">
                <Input placeholder="Désignation" className="col-span-3" {...register(`lignes.${i}.designation`)} />
                <Input placeholder="Unité" className="col-span-1" {...register(`lignes.${i}.unite`)} />
                <Input type="number" step="0.001" placeholder="Qté" className="col-span-2" {...register(`lignes.${i}.quantite`)} />
                <Input type="number" step="1" placeholder="P.U. HT" className="col-span-2" {...register(`lignes.${i}.prix_unitaire`)} />
                <select className={`${selectCls} col-span-3`} {...register(`lignes.${i}.article_stock_id`)}>
                  <option value="">— Article stock (option) —</option>
                  {articles?.items.filter((a) => a.stockable).map((a) => <option key={a.id} value={a.id}>{a.designation}</option>)}
                </select>
                <Button variant="ghost" size="icon" className="col-span-1 h-10 text-status-stuck" onClick={() => remove(i)} disabled={fields.length === 1} aria-label="Retirer"><Trash2 className="size-4" /></Button>
              </div>
            ))}
          </div>
          <div className="mt-2 flex items-center justify-between">
            <p className="text-xs text-content-muted">L'article de stock lié (facultatif) sera repris sur le bon de commande pour l'entrée automatique en stock à la réception.</p>
            <p className="text-sm text-content-secondary">Total HT estimé : <span className="font-bold tabular-nums text-brand-primary">{formatDZD(totalHt)}</span></p>
          </div>
        </div>
        <FormField label="Notes" className="mt-3"><Input {...register('notes')} /></FormField>
      </Modal>
    </div>
  );
}

const selectCls = 'flex h-10 w-full rounded-md border border-border bg-surface-secondary px-3 text-sm';

interface BCForm {
  fournisseur_id: string;
  chantier_id: string;
  tva_pct: number;
  date_livraison_prevue: string;
  conditions_paiement: string;
  delai_livraison: string;
  notes: string;
  lignes: { designation: string; unite: string; quantite: number; prix_unitaire: number }[];
}

// Buckets de filtre sémantiques (Axe 5) : statut brut + présence d'une facture.
const BC_BUCKETS: { key: string; label: string; match: (statut: string, factured: boolean) => boolean }[] = [
  { key: 'BROUILLON', label: 'Brouillon', match: (s) => s === 'BROUILLON' },
  { key: 'VALIDE', label: 'Validé', match: (s) => s === 'ENVOYE' },
  { key: 'A_RECEVOIR', label: 'À réceptionner', match: (s) => s === 'ENVOYE' || s === 'CONFIRME' || s === 'PARTIELLEMENT_LIVRE' },
  { key: 'A_FACTURER', label: 'À facturer', match: (s, f) => s === 'LIVRE' && !f },
  { key: 'FACTURE', label: 'Facturé', match: (_s, f) => f },
  { key: 'ANNULE', label: 'Annulé', match: (s) => s === 'ANNULE' },
];

function BonsCommandeTab() {
  const queryClient = useQueryClient();
  const perms = usePerms('achats');
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [reception, setReception] = useState<BCDTO | null>(null);
  const [detail, setDetail] = useState<DetailDoc | null>(null);
  const [q, setQ] = useState('');
  const [stf, setStf] = useState('');
  const { data, isLoading } = useQuery({ queryKey: ['bons-commande'], queryFn: listBC });
  const { data: fournisseurs } = useQuery({ queryKey: ['tiers', 'fournisseurs'], queryFn: () => listTiers({ type: 'FOURNISSEUR', size: 200 }), enabled: open || !!detail });
  const { data: chantiers } = useQuery({ queryKey: ['chantiers'], queryFn: () => listChantiers(), enabled: open || !!detail });
  // Facturé = BC référencé par une facture validée/payée (cycle Brouillon→Validé→Réceptionné→Facturé).
  const { data: factures } = useQuery({ queryKey: ['factures'], queryFn: () => listFactures() });
  const facturedBcIds = new Set((factures?.items ?? []).filter((f) => f.bc_id && (f.statut === 'VALIDEE' || f.statut === 'PAYEE')).map((f) => f.bc_id as string));

  const bcToDetail = (bc: BCDTO): DetailDoc => ({
    titre: 'Bon de commande',
    reference: bc.reference,
    statut: { label: BC_STATUT_UI[bc.statut].label, key: BC_STATUT_UI[bc.statut].status },
    entete: [
      { label: 'Fournisseur', value: fournisseurs?.items.find((t) => t.id === bc.fournisseur_id)?.raison_sociale ?? null },
      { label: 'Chantier (imputation)', value: (() => { const c = chantiers?.items.find((x) => x.id === bc.chantier_id); return c ? `${c.code} · ${c.nom}` : null; })() },
      { label: 'Date de commande', value: bc.date_commande ? formatDateDZ(bc.date_commande) : null },
      { label: 'Conditions de paiement', value: bc.conditions_paiement },
      { label: 'Délai de livraison', value: bc.delai_livraison },
      { label: 'Notes', value: bc.notes },
    ],
    lignes: bc.lignes.map((l) => ({ designation: l.designation, unite: l.unite, quantite: l.quantite, quantite_recue: l.quantite_recue, prix_unitaire: l.prix_unitaire, montant: l.montant_ligne })),
    avecMontants: true,
    totaux: { ht: bc.montant_ht, tva: bc.tva_montant, ttc: bc.montant_ttc, tva_pct: bc.tva_pct },
  });

  const { register, handleSubmit, watch, control, reset, setValue } = useForm<BCForm>({
    defaultValues: { tva_pct: 19, lignes: [{ designation: '', unite: 'u', quantite: 1, prix_unitaire: 0 }] },
  });
  // Création d'un fournisseur « à la volée » (sans quitter le BC).
  const [creerFrn, setCreerFrn] = useState(false);
  const [nf, setNf] = useState({ raison_sociale: '', nif: '', rc: '', telephone: '' });
  const creerFrnMut = useMutation({
    mutationFn: () => {
      if (!nf.raison_sociale.trim()) return Promise.reject(new Error('La raison sociale est obligatoire.'));
      if (!nf.nif.trim() && !nf.rc.trim()) return Promise.reject(new Error('Renseignez au moins le NIF ou le RC.'));
      return createTiers({ code: '', type: 'FOURNISSEUR', raison_sociale: nf.raison_sociale.trim(), nif: nf.nif.trim() || null, rc: nf.rc.trim() || null, telephone: nf.telephone.trim() || null });
    },
    onSuccess: (t) => {
      // Ajoute le fournisseur au cache IMMÉDIATEMENT pour que l'<option> existe avant le
      // setValue (sinon le <select> rejette la valeur et le fournisseur n'est pas pris).
      queryClient.setQueryData(['tiers', 'fournisseurs'], (old: { items?: TiersDTO[] } | undefined) => {
        const items = old?.items ?? [];
        return items.some((x) => x.id === t.id) ? old : { ...(old ?? { total: 0, page: 1, size: 200, pages: 1 }), items: [t, ...items] };
      });
      setValue('fournisseur_id', t.id, { shouldValidate: true, shouldDirty: true });
      queryClient.invalidateQueries({ queryKey: ['tiers', 'fournisseurs'] });
      setCreerFrn(false); setNf({ raison_sociale: '', nif: '', rc: '', telephone: '' });
      toast.success(`Fournisseur « ${t.raison_sociale} » créé et sélectionné.`);
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const { fields, append, remove } = useFieldArray({ control, name: 'lignes' });
  const lignes = watch('lignes');
  const tva = parseFloat(String(watch('tva_pct') || 0));
  const ht = (lignes ?? []).reduce((s, l) => s + (Number(l.quantite) || 0) * (Number(l.prix_unitaire) || 0), 0);
  const ttc = Math.round(ht * (1 + tva / 100) * 100) / 100;

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['bons-commande'] });
  const resetForm = () => reset({ tva_pct: 19, fournisseur_id: '', chantier_id: '', date_livraison_prevue: '', conditions_paiement: '', delai_livraison: '', notes: '', lignes: [{ designation: '', unite: 'u', quantite: 1, prix_unitaire: 0 }] });

  const createMutation = useMutation({
    mutationFn: (v: BCForm) =>
      createBC({
        fournisseur_id: v.fournisseur_id || null,
        chantier_id: v.chantier_id || null,
        tva_pct: Number(v.tva_pct) / 100,
        date_livraison_prevue: v.date_livraison_prevue || null,
        conditions_paiement: v.conditions_paiement || null,
        delai_livraison: v.delai_livraison || null,
        notes: v.notes || null,
        lignes: v.lignes.filter((l) => l.designation.trim()).map((l) => ({
          designation: l.designation, unite: l.unite || null,
          quantite: Number(l.quantite) || 0, prix_unitaire: Number(l.prix_unitaire) || 0,
        })),
      }),
    onSuccess: () => { invalidate(); setOpen(false); resetForm(); toast.success('Bon de commande créé (brouillon).'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, v }: { id: string; v: BCForm }) =>
      updateBC(id, {
        fournisseur_id: v.fournisseur_id || null,
        chantier_id: v.chantier_id || null,
        tva_pct: Number(v.tva_pct) / 100,
        date_livraison_prevue: v.date_livraison_prevue || null,
        conditions_paiement: v.conditions_paiement || null,
        delai_livraison: v.delai_livraison || null,
        notes: v.notes || null,
        lignes: v.lignes.filter((l) => l.designation.trim()).map((l) => ({
          designation: l.designation, unite: l.unite || null,
          quantite: Number(l.quantite) || 0, prix_unitaire: Number(l.prix_unitaire) || 0,
        })),
      }),
    onSuccess: () => { invalidate(); setOpen(false); setEditId(null); resetForm(); toast.success('Bon de commande modifié.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const openEdit = (bc: BCDTO) => {
    reset({
      fournisseur_id: bc.fournisseur_id ?? '',
      chantier_id: bc.chantier_id ?? '',
      tva_pct: Math.round(Number(bc.tva_pct) * 100),
      date_livraison_prevue: bc.date_livraison_prevue ?? '',
      conditions_paiement: bc.conditions_paiement ?? '',
      delai_livraison: bc.delai_livraison ?? '',
      notes: bc.notes ?? '',
      lignes: bc.lignes.length
        ? bc.lignes.map((l) => ({ designation: l.designation, unite: l.unite ?? 'u', quantite: Number(l.quantite), prix_unitaire: Number(l.prix_unitaire) }))
        : [{ designation: '', unite: 'u', quantite: 1, prix_unitaire: 0 }],
    });
    setEditId(bc.id);
    setOpen(true);
  };

  const validerMutation = useMutation({
    mutationFn: (id: string) => validerBC(id),
    onSuccess: (bc) => { invalidate(); toast.success(`Bon de commande validé : ${bc.reference}.`); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const columns: ColumnDef<BCDTO, unknown>[] = [
    { accessorKey: 'reference', header: 'Référence', size: 150, cell: ({ getValue }) => (getValue() as string) ?? <span className="text-content-muted italic">Brouillon</span> },
    { accessorKey: 'date_commande', header: 'Date', size: 100, cell: ({ getValue }) => (getValue() as string) ?? '—' },
    { id: 'lignes', header: 'Lignes', size: 70, cell: ({ row }) => `${row.original.lignes.length}` },
    { accessorKey: 'montant_ht', header: 'HT', cell: ({ getValue }) => <span className="tabular-nums">{formatDZD(getValue() as number)}</span> },
    { accessorKey: 'montant_ttc', header: 'TTC', cell: ({ getValue }) => <span className="tabular-nums font-medium">{formatDZD(getValue() as number)}</span> },
    {
      accessorKey: 'statut',
      header: 'Statut',
      size: 150,
      cell: ({ row, getValue }) => {
        const ui = BC_STATUT_UI[getValue() as string];
        return (
          <div className="flex flex-wrap items-center gap-1.5">
            <StatusBadge status={ui.status} label={ui.label} />
            {facturedBcIds.has(row.original.id) && <StatusBadge status="done" label="Facturé" />}
          </div>
        );
      },
    },
    {
      id: 'actions',
      header: '',
      size: 230,
      cell: ({ row }) => {
        const bc = row.original;
        return (
          <div className="flex justify-end gap-1">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setDetail(bcToDetail(bc))} aria-label="Voir le détail"><Eye className="size-4" /></Button>
            {bc.statut === 'BROUILLON' && (
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(bc)} aria-label="Modifier"><Pencil className="size-4" /></Button>
            )}
            {bc.statut === 'BROUILLON' && (
              <Button variant="ghost" size="icon" className="h-8 w-8 text-status-done" onClick={() => validerMutation.mutate(bc.id)} disabled={validerMutation.isPending} aria-label="Valider (attribuer le n°)"><CheckCircle2 className="size-4" /></Button>
            )}
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openBCPdf(bc.id, 'open').catch((e) => toast.error(getApiErrorMessage(e)))} aria-label="Imprimer le BC"><Printer className="size-4" /></Button>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openBCPdf(bc.id, 'download').catch((e) => toast.error(getApiErrorMessage(e)))} aria-label="Télécharger le BC"><Download className="size-4" /></Button>
            {(bc.statut === 'CONFIRME' || bc.statut === 'ENVOYE' || bc.statut === 'PARTIELLEMENT_LIVRE') && (
              <Button variant="ghost" size="icon" className="h-8 w-8 text-brand-primary" onClick={() => setReception(bc)} aria-label="Réceptionner"><Truck className="size-4" /></Button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        {perms.canAjouter && <Button onClick={() => { setEditId(null); resetForm(); setOpen(true); }}>
          <Plus /> Nouveau bon de commande
        </Button>}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Input placeholder="Rechercher (n°…)" value={q} onChange={(e) => setQ(e.target.value)} className="h-9 max-w-xs" />
        <select value={stf} onChange={(e) => setStf(e.target.value)} className="h-9 rounded-md border border-border bg-surface-secondary px-2 text-sm">
          <option value="">Tous les statuts</option>
          {BC_BUCKETS.map((b) => <option key={b.key} value={b.key}>{b.label}</option>)}
        </select>
      </div>
      <DataTable
        columns={columns}
        data={(data?.items ?? []).filter((r) => {
          if (!stf) return true;
          const bucket = BC_BUCKETS.find((b) => b.key === stf);
          return bucket ? bucket.match(r.statut, facturedBcIds.has(r.id)) : true;
        })}
        globalFilter={q}
        loading={isLoading}
        pageSize={15}
        exportFileName="bons-commande"
        emptyMessage="Aucun bon de commande."
      />
      <DetailDocumentModal open={!!detail} onClose={() => setDetail(null)} doc={detail} />

      <Modal
        open={open}
        onOpenChange={(o) => { setOpen(o); if (!o) setEditId(null); }}
        title={editId ? 'Modifier le bon de commande' : 'Nouveau bon de commande'}
        size="xl"
        footer={
          <>
            <Button variant="secondary" onClick={() => { setOpen(false); setEditId(null); }}>Annuler</Button>
            {editId
              ? <Button onClick={handleSubmit((v) => updateMutation.mutate({ id: editId, v }))} loading={updateMutation.isPending}>Enregistrer</Button>
              : <Button onClick={handleSubmit((v) => createMutation.mutate(v))} loading={createMutation.isPending}>Créer (brouillon)</Button>}
          </>
        }
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField label="Fournisseur" required className="sm:col-span-2" hint="Obligatoire et choisi dans le référentiel — un BC engage l'entreprise.">
            <div className="flex gap-2">
              <select {...register('fournisseur_id')} className={cn(selectCls, 'flex-1')}>
                <option value="">— Sélectionner un fournisseur —</option>
                {fournisseurs?.items.map((t) => <option key={t.id} value={t.id}>{t.code} · {t.raison_sociale}</option>)}
              </select>
              <Button type="button" variant="secondary" onClick={() => setCreerFrn((v) => !v)}><UserPlus className="size-4" /> Créer</Button>
            </div>
            {creerFrn && (
              <div className="mt-2 space-y-2 rounded-md border border-border bg-surface-primary p-3">
                <p className="text-xs font-semibold text-content-secondary">Nouveau fournisseur (enregistré au référentiel)</p>
                <Input placeholder="Raison sociale *" value={nf.raison_sociale} onChange={(e) => setNf({ ...nf, raison_sociale: e.target.value })} />
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                  <Input placeholder="NIF" value={nf.nif} onChange={(e) => setNf({ ...nf, nif: e.target.value })} />
                  <Input placeholder="RC" value={nf.rc} onChange={(e) => setNf({ ...nf, rc: e.target.value })} />
                  <Input placeholder="Téléphone" value={nf.telephone} onChange={(e) => setNf({ ...nf, telephone: e.target.value })} />
                </div>
                <p className="text-xs text-content-muted">Renseignez au minimum le <strong>NIF ou le RC</strong> (facture conforme).</p>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="secondary" size="sm" onClick={() => setCreerFrn(false)}>Annuler</Button>
                  <Button type="button" size="sm" onClick={() => creerFrnMut.mutate()} loading={creerFrnMut.isPending}>Enregistrer le fournisseur</Button>
                </div>
              </div>
            )}
          </FormField>
          <FormField label="Chantier (imputation)">
            <select {...register('chantier_id')} className={selectCls}>
              <option value="">— Sélectionner —</option>
              {chantiers?.items.map((c) => <option key={c.id} value={c.id}>{c.code} · {c.nom}</option>)}
            </select>
          </FormField>
          <FormField label="Livraison prévue"><Input type="date" {...register('date_livraison_prevue')} /></FormField>
          <FormField label="Conditions de paiement"><Input placeholder="ex : 30 jours fin de mois" {...register('conditions_paiement')} /></FormField>
          <FormField label="Délai de livraison"><Input placeholder="ex : 2 semaines" {...register('delai_livraison')} /></FormField>
        </div>

        <div className="mt-4">
          <div className="mb-2 flex items-center justify-between">
            <p className="label-caps">Lignes de commande</p>
            <Button variant="ghost" size="sm" onClick={() => append({ designation: '', unite: 'u', quantite: 1, prix_unitaire: 0 })}><Plus className="size-4" /> Ligne</Button>
          </div>
          <div className="space-y-2">
            {fields.map((f, i) => (
              <div key={f.id} className="grid grid-cols-12 gap-2">
                <Input placeholder="Désignation" className="col-span-5" {...register(`lignes.${i}.designation`)} />
                <select className={`${selectCls} col-span-2`} {...register(`lignes.${i}.unite`)}>
                  {UNITES_ACHAT.map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
                <Input type="number" step="0.001" placeholder="Qté" className="col-span-2" {...register(`lignes.${i}.quantite`)} />
                <Input type="number" step="1" placeholder="P.U. HT" className="col-span-2" {...register(`lignes.${i}.prix_unitaire`)} />
                <Button variant="ghost" size="icon" className="col-span-1 h-10 text-status-stuck" onClick={() => remove(i)} disabled={fields.length === 1} aria-label="Retirer"><Trash2 className="size-4" /></Button>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField label="TVA (%)"><Input type="number" step="any" {...register('tva_pct')} /></FormField>
          <FormField label="Notes"><Input {...register('notes')} /></FormField>
          <div className="sm:col-span-2 flex justify-end gap-6 rounded-md bg-surface-hover px-4 py-2.5 text-sm">
            <span>HT : <span className="font-semibold tabular-nums">{formatDZD(ht)}</span></span>
            <span>TTC : <span className="font-bold tabular-nums text-brand-primary">{formatDZD(ttc)}</span></span>
          </div>
        </div>
      </Modal>

      <ReceptionModal bc={reception} onClose={() => setReception(null)} onDone={invalidate} />
    </div>
  );
}

const REC_STATUT_UI: Record<string, { label: string; status: StatusKey }> = {
  CONFORME: { label: 'Conforme', status: 'done' },
  NON_CONFORME: { label: 'Non conforme', status: 'stuck' },
};

const recToDetail = (r: ReceptionDTO): DetailDoc => ({
  titre: 'Bon de réception',
  reference: r.reference,
  statut: { label: REC_STATUT_UI[r.statut].label, key: REC_STATUT_UI[r.statut].status },
  entete: [
    { label: 'Bon de commande', value: r.bc_reference },
    { label: 'Fournisseur', value: r.fournisseur_nom },
    { label: 'Date de réception', value: r.date_reception ? formatDateDZ(r.date_reception) : null },
    { label: 'BL fournisseur', value: r.reference_bl_fournisseur },
    { label: 'Montant reçu', value: formatDZD(r.montant_recu) },
    { label: 'Notes', value: r.notes },
  ],
  lignes: r.lignes.map((l) => ({ designation: l.article, unite: l.unite, quantite: l.quantite_commandee, quantite_recue: l.quantite_recue, prix_unitaire: l.prix_unitaire, montant: l.montant })),
  avecRecu: true,
  avecMontants: true,
  totaux: null,
});

function ReceptionsTab() {
  const queryClient = useQueryClient();
  const [reception, setReception] = useState<BCDTO | null>(null);
  const [detail, setDetail] = useState<DetailDoc | null>(null);
  const { data: bcs, isLoading: bcLoading } = useQuery({ queryKey: ['bons-commande'], queryFn: listBC });
  const { data: receptions, isLoading: recLoading } = useQuery({ queryKey: ['receptions'], queryFn: listReceptions });
  const invalidate = () => { queryClient.invalidateQueries({ queryKey: ['receptions'] }); queryClient.invalidateQueries({ queryKey: ['bons-commande'] }); };

  const aRecevoir = (bcs?.items ?? []).filter((bc) => ['CONFIRME', 'ENVOYE', 'PARTIELLEMENT_LIVRE'].includes(bc.statut));

  const colsARecevoir: ColumnDef<BCDTO, unknown>[] = [
    { accessorKey: 'reference', header: 'Bon de commande', size: 160, cell: ({ getValue }) => (getValue() as string) ?? '—' },
    { accessorKey: 'date_commande', header: 'Date', size: 110, cell: ({ getValue }) => (getValue() as string) ?? '—' },
    { accessorKey: 'montant_ttc', header: 'Montant TTC', cell: ({ getValue }) => <span className="tabular-nums">{formatDZD(getValue() as number)}</span> },
    { accessorKey: 'statut', header: 'Statut', size: 120, cell: ({ getValue }) => { const ui = BC_STATUT_UI[getValue() as string]; return <StatusBadge status={ui.status} label={ui.label} />; } },
    { id: 'actions', header: '', size: 160, cell: ({ row }) => <div className="flex justify-end"><Button size="sm" onClick={() => setReception(row.original)}><Truck className="size-4" /> Réceptionner</Button></div> },
  ];

  const colsReceptions: ColumnDef<ReceptionDTO, unknown>[] = [
    { accessorKey: 'reference', header: 'N° réception', size: 150, cell: ({ getValue }) => (getValue() as string) ?? <span className="text-content-muted italic">—</span> },
    { accessorKey: 'date_reception', header: 'Date', size: 110, cell: ({ getValue }) => (getValue() as string) ?? '—' },
    { accessorKey: 'bc_reference', header: 'Bon de commande', size: 150, cell: ({ getValue }) => (getValue() as string) ?? '—' },
    { accessorKey: 'fournisseur_nom', header: 'Fournisseur', cell: ({ getValue }) => (getValue() as string) ?? '—' },
    { accessorKey: 'montant_recu', header: 'Montant reçu', size: 130, cell: ({ getValue }) => <span className="tabular-nums font-medium">{formatDZD(getValue() as number)}</span> },
    { accessorKey: 'statut', header: 'Statut', size: 130, cell: ({ getValue }) => { const ui = REC_STATUT_UI[getValue() as string]; return <StatusBadge status={ui.status} label={ui.label} />; } },
    { id: 'actions', header: '', size: 96, cell: ({ row }) => <div className="flex justify-end gap-1"><Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openReceptionPdf(row.original.id, 'open').catch((e) => toast.error(getApiErrorMessage(e)))} aria-label="Imprimer le bon de réception"><Printer className="size-4" /></Button><Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setDetail(recToDetail(row.original))} aria-label="Voir le détail"><Eye className="size-4" /></Button></div> },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-content-primary"><Truck className="size-4 text-brand-primary" /> Commandes à réceptionner</h3>
        <p className="mb-2 text-xs text-content-muted">Bons de commande validés (confirmés / partiellement livrés). Cliquez sur « Réceptionner » pour enregistrer une livraison (totale ou partielle) ; l'entrée en stock se fait automatiquement si un dépôt est choisi.</p>
        <DataTable columns={colsARecevoir} data={aRecevoir} loading={bcLoading} pageSize={10} emptyMessage="Aucune commande en attente de réception." />
      </div>
      <div>
        <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-content-primary"><PackageCheck className="size-4 text-status-done" /> Réceptions effectuées</h3>
        <DataTable columns={colsReceptions} data={receptions?.items ?? []} loading={recLoading} pageSize={15} exportFileName="receptions" emptyMessage="Aucune réception enregistrée." />
      </div>
      <ReceptionModal bc={reception} onClose={() => setReception(null)} onDone={invalidate} />
      <DetailDocumentModal open={!!detail} onClose={() => setDetail(null)} doc={detail} />
    </div>
  );
}

function ReceptionModal({ bc, onClose, onDone }: { bc: BCDTO | null; onClose: () => void; onDone: () => void }) {
  const queryClient = useQueryClient();
  const [qtes, setQtes] = useState<Record<string, string>>({});
  const [depotId, setDepotId] = useState('');
  const { data: depots } = useQuery({ queryKey: ['depots'], queryFn: listDepots, enabled: !!bc });
  const restant = (l: BCDTO['lignes'][number]) => Math.max(0, Number(l.quantite) - Number(l.quantite_recue));

  const mutation = useMutation({
    mutationFn: () => createReception({
      bc_id: bc!.id,
      depot_id: depotId || null,
      statut: 'CONFORME',
      date_reception: new Date().toISOString().slice(0, 10),
      lignes: bc!.lignes
        .map((l) => ({ bc_ligne_id: l.id, quantite_recue: Number(qtes[l.id] ?? restant(l)) }))
        .filter((x) => x.quantite_recue > 0),
    }),
    onSuccess: () => { onDone(); queryClient.invalidateQueries({ queryKey: ['articles'] }); onClose(); toast.success('Réception enregistrée.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  return (
    <Modal
      open={!!bc}
      onOpenChange={(o) => { if (!o) onClose(); }}
      title={`Réception — ${bc?.reference ?? ''}`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Annuler</Button>
          <Button onClick={() => mutation.mutate()} loading={mutation.isPending}>Enregistrer la réception</Button>
        </>
      }
    >
      <FormField label="Dépôt d'entrée (pour la mise en stock des articles)" className="mb-3">
        <select value={depotId} onChange={(e) => setDepotId(e.target.value)} className="flex h-10 w-full rounded-md border border-border bg-surface-secondary px-3 text-sm">
          <option value="">— Aucun (pas d'entrée stock) —</option>
          {depots?.map((d) => <option key={d.id} value={d.id}>{d.code} · {d.libelle}</option>)}
        </select>
      </FormField>
      <p className="mb-3 text-xs text-content-muted">Saisissez les quantités reçues (par défaut le reste à livrer). Une réception partielle est possible.</p>
      <div className="space-y-2">
        {bc?.lignes.map((l) => (
          <div key={l.id} className="grid grid-cols-12 items-center gap-2 text-sm">
            <span className="col-span-6 truncate">{l.designation}</span>
            <span className="col-span-3 text-right tabular-nums text-content-muted">cmd {Number(l.quantite)} {l.unite} · reçu {Number(l.quantite_recue)}</span>
            <Input type="number" step="0.001" className="col-span-3" defaultValue={restant(l)} onChange={(e) => setQtes((q) => ({ ...q, [l.id]: e.target.value }))} />
          </div>
        ))}
      </div>
    </Modal>
  );
}


interface FactureForm {
  type_doc: string;       // FACTURE / AVOIR
  comptabilisee: boolean;
  tiers_type: string;     // FOURNISSEUR / SOUS_TRAITANT
  fournisseur_id: string; // = tiers_id
  bc_id: string;
  chantier_id: string;
  numero_facture: string;
  date_facture: string;
  date_echeance: string;
  montant_ht: number;
  tva_taux: string;       // valeur du select (0.19 / 0.09 / 0.07 / 0 / EX)
  tva_montant: number | '';
  montant_ttc: number | '';
  mode_paiement: string;
  paiement_reference: string;
  paiement_banque: string;
  paiement_date: string;
  paiement_echeance: string;
  statut: string;
  date_paiement: string;
  notes: string;
}

export function FacturesTab() {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [rappro, setRappro] = useState<FactureDTO | null>(null);
  const [paiementsFor, setPaiementsFor] = useState<FactureDTO | null>(null);
  const [detail, setDetail] = useState<FactureDTO | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const [pieces, setPieces] = useState<PieceJointe[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);
  const [filtres, setFiltres] = useState<FactureFiltres>({});
  const setF = (patch: Partial<FactureFiltres>) => setFiltres((f) => ({ ...f, ...patch }));
  const { data, isLoading } = useQuery({ queryKey: ['factures', filtres], queryFn: () => listFactures(filtres) });
  const { data: totaux } = useQuery({ queryKey: ['factures-totaux', filtres], queryFn: () => getFacturesTotaux(filtres) });
  const { data: bcs } = useQuery({ queryKey: ['bons-commande'], queryFn: listBC, enabled: open });
  const { data: fournisseurs } = useQuery({ queryKey: ['tiers', 'FOURNISSEUR'], queryFn: () => listTiers({ type: 'FOURNISSEUR', size: 200 }) });
  const { data: sousTraitants } = useQuery({ queryKey: ['tiers', 'SOUS_TRAITANT'], queryFn: () => listTiers({ type: 'SOUS_TRAITANT', size: 200 }) });
  const { data: chantiersData } = useQuery({ queryKey: ['chantiers'], queryFn: () => listChantiers() });
  const fournItems = fournisseurs?.items ?? [];
  const stItems = sousTraitants?.items ?? [];
  const tiersMap = new Map([...fournItems, ...stItems].map((t) => [t.id, t.raison_sociale]));

  const FORM_DEFAULTS: Partial<FactureForm> = { type_doc: 'FACTURE', comptabilisee: false, tiers_type: 'FOURNISSEUR', tva_taux: '0.19', statut: 'BROUILLON', mode_paiement: '', tva_montant: '', montant_ttc: '' };
  const { register, handleSubmit, reset, setValue, watch } = useForm<FactureForm>({ defaultValues: FORM_DEFAULTS });
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['factures'] });
    queryClient.invalidateQueries({ queryKey: ['factures-totaux'] });
  };

  // Tiers proposés selon le type choisi + mode de paiement (détail conditionnel).
  const formTiersType = (watch('tiers_type') || 'FOURNISSEUR') as TiersTypeFacture;
  const tiersList = formTiersType === 'SOUS_TRAITANT' ? stItems : fournItems;

  // Aperçu live HT → TVA → TTC ; les montants TVA/TTC restent surchargeables.
  const htW = Number(watch('montant_ht')) || 0;
  const tauxSel = TVA_TAUX_FACTURE.find((t) => t.value === watch('tva_taux')) ?? TVA_TAUX_FACTURE[0];
  const tvaAuto = Math.round(htW * tauxSel.pct * 100) / 100;
  const ttcAuto = Math.round((htW + tvaAuto) * 100) / 100;

  // Création d'un tiers « à la volée » (fournisseur ou sous-traitant), sans quitter la facture.
  const [creerFrn, setCreerFrn] = useState(false);
  const [nf, setNf] = useState({ raison_sociale: '', nif: '', nis: '', rc: '' });
  const creerFrnMut = useMutation({
    mutationFn: () => {
      if (!nf.raison_sociale.trim()) return Promise.reject(new Error('La raison sociale est obligatoire.'));
      if (!nf.nif.trim() && !nf.rc.trim()) return Promise.reject(new Error('Renseignez au moins le NIF ou le RC (facture conforme).'));
      return createTiers({ code: '', type: formTiersType, raison_sociale: nf.raison_sociale.trim(), nif: nf.nif.trim() || null, nis: nf.nis.trim() || null, rc: nf.rc.trim() || null });
    },
    onSuccess: (t) => {
      // Ajoute le tiers au cache IMMÉDIATEMENT (même clé que le <select>) pour que
      // l'<option> existe avant le setValue, sinon le tiers n'est pas pris en charge.
      queryClient.setQueryData(['tiers', formTiersType], (old: { items?: TiersDTO[] } | undefined) => {
        const items = old?.items ?? [];
        return items.some((x) => x.id === t.id) ? old : { ...(old ?? { total: 0, page: 1, size: 200, pages: 1 }), items: [t, ...items] };
      });
      setValue('fournisseur_id', t.id, { shouldValidate: true, shouldDirty: true });
      queryClient.invalidateQueries({ queryKey: ['tiers', formTiersType] });
      setCreerFrn(false); setNf({ raison_sociale: '', nif: '', nis: '', rc: '' });
      toast.success(`Tiers « ${t.raison_sociale} » créé et sélectionné.`);
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const createMutation = useMutation({
    mutationFn: (v: FactureForm) => {
      const ts = TVA_TAUX_FACTURE.find((t) => t.value === v.tva_taux) ?? TVA_TAUX_FACTURE[0];
      return createFacture({
        type_doc: (v.type_doc || 'FACTURE') as TypeDocFacture,
        comptabilisee: !!v.comptabilisee,
        tiers_type: (v.tiers_type || 'FOURNISSEUR') as TiersTypeFacture,
        fournisseur_id: v.fournisseur_id || null,
        bc_id: v.bc_id || null,
        chantier_id: v.chantier_id || null,
        numero_facture: v.numero_facture || null,
        date_facture: v.date_facture || null,
        date_echeance: v.date_echeance || null,
        montant_ht: Number(v.montant_ht) || 0,
        tva_pct: ts.pct,
        tva_exonere: ts.exonere,
        tva_montant: v.tva_montant === '' ? null : Number(v.tva_montant),
        montant_ttc: v.montant_ttc === '' ? null : Number(v.montant_ttc),
        pieces,
        notes: v.notes || null,
      });
    },
    onSuccess: () => { invalidate(); setOpen(false); reset(FORM_DEFAULTS); setPieces([]); toast.success('Facture enregistrée.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  // Construit la charge utile (création / modification) à partir du formulaire.
  const buildPayload = (v: FactureForm) => {
    const ts = TVA_TAUX_FACTURE.find((t) => t.value === v.tva_taux) ?? TVA_TAUX_FACTURE[0];
    return {
      type_doc: (v.type_doc || 'FACTURE') as TypeDocFacture,
      comptabilisee: !!v.comptabilisee,
      tiers_type: (v.tiers_type || 'FOURNISSEUR') as TiersTypeFacture,
      fournisseur_id: v.fournisseur_id || null,
      bc_id: v.bc_id || null,
      chantier_id: v.chantier_id || null,
      numero_facture: v.numero_facture || null,
      date_facture: v.date_facture || null,
      date_echeance: v.date_echeance || null,
      montant_ht: Number(v.montant_ht) || 0,
      tva_pct: ts.pct,
      tva_exonere: ts.exonere,
      tva_montant: v.tva_montant === '' ? null : Number(v.tva_montant),
      montant_ttc: v.montant_ttc === '' ? null : Number(v.montant_ttc),
      pieces,
      notes: v.notes || null,
    };
  };

  const updateMutation = useMutation({
    mutationFn: (v: FactureForm) => updateFacture(editId!, buildPayload(v)),
    onSuccess: () => { invalidate(); setOpen(false); setEditId(null); reset(FORM_DEFAULTS); setPieces([]); toast.success('Facture modifiée.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  // Pré-remplit le formulaire pour modifier une facture existante.
  const openEdit = (f: FactureDTO) => {
    const taux = f.tva_exonere
      ? (TVA_TAUX_FACTURE.find((t) => t.exonere)?.value ?? '0')
      : (TVA_TAUX_FACTURE.find((t) => !t.exonere && t.pct === f.tva_pct)?.value ?? '0.19');
    reset({
      type_doc: f.type_doc, comptabilisee: f.comptabilisee,
      tiers_type: f.tiers_type ?? 'FOURNISSEUR',
      fournisseur_id: f.fournisseur_id ?? '', bc_id: f.bc_id ?? '', chantier_id: f.chantier_id ?? '',
      numero_facture: f.numero_facture ?? '', date_facture: f.date_facture ?? '', date_echeance: f.date_echeance ?? '',
      montant_ht: f.montant_ht, tva_taux: taux,
      tva_montant: f.tva_exonere ? '' : f.tva_montant, montant_ttc: f.montant_ttc,
      statut: f.statut, notes: f.notes ?? '',
    });
    setPieces(f.pieces ?? []);
    setCreerFrn(false);
    setEditId(f.id);
    setOpen(true);
  };

  const validerMutation = useMutation({ mutationFn: validerFacture, onSuccess: (f) => { invalidate(); toast.success(`Facture validée : ${f.reference}.`); }, onError: (e) => toast.error(getApiErrorMessage(e)) });
  const deleteMutation = useMutation({ mutationFn: deleteFacture, onSuccess: () => { invalidate(); toast.success('Facture supprimée.'); }, onError: (e) => toast.error(getApiErrorMessage(e)) });

  const handleScan = async (file: File) => {
    try { const up = await uploadFile(file); setPieces((p) => [...p, { url: up.url, nom: up.filename }]); toast.success('Pièce jointe ajoutée.'); }
    catch (e) { toast.error(getApiErrorMessage(e)); }
  };

  const columns: ColumnDef<FactureDTO, unknown>[] = [
    { accessorKey: 'numero_facture', header: 'N° facture', size: 116, cell: ({ getValue }) => (getValue() as string) ?? <span className="text-content-muted italic">—</span> },
    { accessorKey: 'date_facture', header: 'Date', size: 92, cell: ({ getValue }) => (getValue() as string) ?? '—' },
    { id: 'type', header: 'Type', size: 52, cell: ({ row }) => { const t = row.original.tiers_type; return <span className="rounded bg-surface-hover px-1.5 py-0.5 text-[10px] font-semibold uppercase text-content-muted">{t === 'SOUS_TRAITANT' ? 'S-T' : t === 'FOURNISSEUR' ? 'Frn' : '—'}</span>; } },
    { id: 'tiers', header: 'Tiers', size: 146, cell: ({ row }) => tiersMap.get(row.original.fournisseur_id ?? '') ?? <span className="text-content-muted">—</span> },
    { accessorKey: 'montant_ht', header: 'HT', size: 96, cell: ({ getValue }) => <span className="tabular-nums">{formatDZD(getValue() as number)}</span> },
    { accessorKey: 'tva_montant', header: 'TVA', size: 92, cell: ({ row }) => row.original.tva_exonere ? <span className="text-[11px] text-content-muted">Exonéré</span> : <span className="tabular-nums">{formatDZD(row.original.tva_montant)}</span> },
    { accessorKey: 'montant_ttc', header: 'TTC', size: 100, cell: ({ getValue }) => <span className="tabular-nums font-medium">{formatDZD(getValue() as number)}</span> },
    { id: 'regle', header: 'Statut', size: 122, cell: ({ row }) => { const sp = row.original.statut_paiement; const ui = sp === 'SOLDEE' ? 'done' : sp === 'PARTIELLE' ? 'working' : 'stuck'; return <div className="flex flex-col gap-0.5"><StatusBadge status={ui} label={STATUT_PAIEMENT_LABEL[sp]} />{sp !== 'SOLDEE' && <span className="text-[10px] tabular-nums text-content-muted">reste {formatDZD(row.original.reste_a_payer)}</span>}</div>; } },
    { id: 'pj', header: 'Pièce', size: 92, cell: ({ row }) => { const has = (row.original.pieces?.length ?? 0) > 0 || !!row.original.justificatif_url; return has ? <span className="inline-flex items-center gap-1 text-status-done"><Paperclip className="size-4" />{(row.original.pieces?.length ?? 0) > 1 ? ` ${row.original.pieces.length}` : ''}</span> : <span className="inline-flex items-center gap-1 rounded bg-status-stuck/10 px-1.5 py-0.5 text-[10px] font-semibold text-status-stuck"><AlertTriangle className="size-3" /> manquante</span>; } },
    {
      id: 'actions',
      header: '',
      size: 256,
      cell: ({ row }) => {
        const f = row.original;
        return (
          <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openFacturePdf(f.id, 'open').catch((e) => toast.error(getApiErrorMessage(e)))} aria-label="Imprimer la facture"><Printer className="size-4" /></Button>
            {f.statut_paiement !== 'SOLDEE' && <Button variant="ghost" size="icon" className="h-8 w-8 text-brand-primary" onClick={() => setPaiementsFor(f)} aria-label="Payer la facture" title="Payer la facture"><CreditCard className="size-4" /></Button>}
            {f.bc_id && <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setRappro(f)} aria-label="Rapprochement"><Scale className="size-4" /></Button>}
            {(f.statut === 'BROUILLON' || f.statut === 'LITIGE') && <Button variant="ghost" size="icon" className="h-8 w-8 text-status-done" onClick={() => validerMutation.mutate(f.id)} aria-label="Valider"><CheckCircle2 className="size-4" /></Button>}
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(f)} aria-label="Modifier"><Pencil className="size-4" /></Button>
            <Button variant="ghost" size="icon" className="h-8 w-8 text-status-stuck" onClick={() => { if (window.confirm('Supprimer cette facture ?')) deleteMutation.mutate(f.id); }} aria-label="Supprimer"><Trash2 className="size-4" /></Button>
          </div>
        );
      },
    },
  ];

  return (
    <div className="space-y-4">
      {/* Filtres comptables (période, fournisseur, statut, chantier, paiement) */}
      <div className="flex flex-wrap items-end gap-2 rounded-lg border border-border bg-surface-secondary p-3 shadow-sm">
        <label className="flex flex-col text-xs text-content-muted" title="Filtre sur la date de la facture">Mois facture
          <select className={selectCls} value={filtres.mois ?? ''} onChange={(e) => setF({ mois: e.target.value ? Number(e.target.value) : null })}>
            <option value="">Tous</option>
            {Array.from({ length: 12 }, (_, i) => <option key={i + 1} value={i + 1}>{String(i + 1).padStart(2, '0')}</option>)}
          </select>
        </label>
        <label className="flex flex-col text-xs text-content-muted" title="Période de déclaration G50 : filtre les paiements par leur mois de déclaration (≠ date de paiement brute)">Mois G50
          <select className={selectCls} value={filtres.mois_paiement ?? ''} onChange={(e) => setF({ mois_paiement: e.target.value ? Number(e.target.value) : null })}>
            <option value="">Tous</option>
            {Array.from({ length: 12 }, (_, i) => <option key={i + 1} value={i + 1}>{String(i + 1).padStart(2, '0')}</option>)}
          </select>
        </label>
        <label className="flex flex-col text-xs text-content-muted">Année
          <input type="number" className={`${selectCls} w-24`} placeholder="Toutes" value={filtres.annee ?? ''} onChange={(e) => setF({ annee: e.target.value ? Number(e.target.value) : null })} />
        </label>
        <label className="flex flex-col text-xs text-content-muted">Type tiers
          <select className={selectCls} value={filtres.tiers_type ?? ''} onChange={(e) => setF({ tiers_type: (e.target.value || null) as FactureFiltres['tiers_type'] })}>
            <option value="">Tous</option>
            {TIERS_TYPES_FACTURE.map((t) => <option key={t} value={t}>{TIERS_TYPE_FACTURE_LABEL[t]}</option>)}
          </select>
        </label>
        <label className="flex flex-col text-xs text-content-muted">Tiers
          <select className={selectCls} value={filtres.fournisseur_id ?? ''} onChange={(e) => setF({ fournisseur_id: e.target.value || null })}>
            <option value="">Tous</option>
            {[...fournItems, ...stItems].map((t) => <option key={t.id} value={t.id}>{t.raison_sociale}</option>)}
          </select>
        </label>
        <label className="flex flex-col text-xs text-content-muted">Étape
          <select className={selectCls} value={filtres.statut ?? ''} onChange={(e) => setF({ statut: (e.target.value || null) as FactureFiltres['statut'] })}>
            <option value="">Tous</option>
            {FACTURE_STATUTS.map((s) => <option key={s} value={s}>{FACTURE_STATUT_LABEL[s]}</option>)}
          </select>
        </label>
        <label className="flex flex-col text-xs text-content-muted">Chantier
          <select className={selectCls} value={filtres.chantier_id ?? ''} onChange={(e) => setF({ chantier_id: e.target.value || null })}>
            <option value="">Tous</option>
            {(chantiersData?.items ?? []).map((c) => <option key={c.id} value={c.id}>{c.code}</option>)}
          </select>
        </label>
        <label className="flex flex-col text-xs text-content-muted">Type
          <select className={selectCls} value={filtres.type_doc ?? ''} onChange={(e) => setF({ type_doc: (e.target.value || null) as FactureFiltres['type_doc'] })}>
            <option value="">Tous</option>
            <option value="FACTURE">Facture</option>
            <option value="AVOIR">Avoir</option>
          </select>
        </label>
        <label className="flex flex-col text-xs text-content-muted">Taux TVA
          <select className={selectCls} value={filtres.tva_pct ?? ''} onChange={(e) => setF({ tva_pct: e.target.value === '' ? null : Number(e.target.value) })}>
            <option value="">Tous</option>
            <option value="0.19">19 %</option>
            <option value="0.09">9 %</option>
            <option value="0.07">7 %</option>
            <option value="0">0 % / Exonéré</option>
          </select>
        </label>
        {(filtres.mois || filtres.mois_paiement || filtres.annee || filtres.fournisseur_id || filtres.statut || filtres.chantier_id || filtres.mode_paiement || filtres.type_doc || filtres.tva_pct != null) && (
          <Button variant="secondary" size="sm" onClick={() => setFiltres({})}>Réinitialiser</Button>
        )}
        <div className="ml-auto">
          <Button onClick={() => { reset(FORM_DEFAULTS); setPieces([]); setCreerFrn(false); setEditId(null); setOpen(true); }}><Plus /> Nouvelle facture</Button>
        </div>
      </div>

      {/* Totaux de la sélection (base future état des fournisseurs / G50) */}
      <div className={`grid grid-cols-2 gap-3 ${filtres.mois_paiement && filtres.annee ? 'sm:grid-cols-5' : 'sm:grid-cols-4'}`}>
        {[
          { l: 'Factures', v: String(totaux?.nombre ?? 0) },
          { l: 'Total HT', v: formatDZD(totaux?.total_ht ?? 0) },
          { l: 'Total TVA', v: formatDZD(totaux?.total_tva ?? 0) },
          { l: 'Total TTC', v: formatDZD(totaux?.total_ttc ?? 0) },
          ...(filtres.mois_paiement && filtres.annee
            ? [{ l: `Payé · G50 ${String(filtres.mois_paiement).padStart(2, '0')}/${filtres.annee}`, v: formatDZD(totaux?.total_paye_periode ?? 0) }]
            : []),
        ].map((k) => (
          <div key={k.l} className="rounded-lg border border-border bg-surface-secondary p-3 shadow-sm">
            <p className="text-xs text-content-muted">{k.l}</p>
            <p className="mt-0.5 text-base font-bold tabular-nums text-content-primary">{k.v}</p>
          </div>
        ))}
      </div>

      <DataTable columns={columns} data={data?.items ?? []} loading={isLoading} pageSize={15} exportFileName="factures" emptyMessage="Aucune facture fournisseur." onRowClick={(f) => setDetail(f)} />

      <Modal
        open={open}
        onOpenChange={(o) => { setOpen(o); if (!o) setEditId(null); }}
        title={editId ? 'Modifier la facture' : 'Nouvelle facture fournisseur'}
        footer={<><Button variant="secondary" onClick={() => { setOpen(false); setEditId(null); }}>Annuler</Button><Button onClick={editId ? handleSubmit((v) => updateMutation.mutate(v)) : handleSubmit((v) => createMutation.mutate(v))} loading={createMutation.isPending || updateMutation.isPending}>Enregistrer</Button></>}
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField label="Type de document">
            <select {...register('type_doc')} className={selectCls}>
              <option value="FACTURE">{TYPE_DOC_LABEL.FACTURE}</option>
              <option value="AVOIR">{TYPE_DOC_LABEL.AVOIR} (montants déduits)</option>
            </select>
          </FormField>
          <FormField label="Type de tiers" required>
            <select {...register('tiers_type')} className={selectCls}>
              {TIERS_TYPES_FACTURE.map((t) => <option key={t} value={t}>{TIERS_TYPE_FACTURE_LABEL[t]}</option>)}
            </select>
          </FormField>
          <FormField label="N° de facture (du tiers)" required><Input placeholder="ex : F-2026/055" {...register('numero_facture')} /></FormField>
          <FormField label={formTiersType === 'SOUS_TRAITANT' ? 'Sous-traitant' : 'Fournisseur'} required className="sm:col-span-2">
            <div className="flex gap-2">
              <select {...register('fournisseur_id')} className={cn(selectCls, 'flex-1')}>
                <option value="">— Sélectionner —</option>
                {tiersList.map((t) => <option key={t.id} value={t.id}>{t.raison_sociale}</option>)}
              </select>
              <Button type="button" variant="secondary" onClick={() => setCreerFrn((v) => !v)}><UserPlus className="size-4" /> Créer</Button>
            </div>
            {creerFrn && (
              <div className="mt-2 space-y-2 rounded-md border border-border bg-surface-primary p-3">
                <p className="text-xs font-semibold text-content-secondary">Nouveau {formTiersType === 'SOUS_TRAITANT' ? 'sous-traitant' : 'fournisseur'} (enregistré au référentiel)</p>
                <Input placeholder="Raison sociale *" value={nf.raison_sociale} onChange={(e) => setNf({ ...nf, raison_sociale: e.target.value })} />
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                  <Input placeholder="NIF" value={nf.nif} onChange={(e) => setNf({ ...nf, nif: e.target.value })} />
                  <Input placeholder="NIS" value={nf.nis} onChange={(e) => setNf({ ...nf, nis: e.target.value })} />
                  <Input placeholder="RC" value={nf.rc} onChange={(e) => setNf({ ...nf, rc: e.target.value })} />
                </div>
                <p className="text-xs text-content-muted">Renseignez au minimum le <strong>NIF ou le RC</strong> ; les infos fiscales restent complétables dans la fiche du tiers.</p>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="secondary" size="sm" onClick={() => setCreerFrn(false)}>Annuler</Button>
                  <Button type="button" size="sm" onClick={() => creerFrnMut.mutate()} loading={creerFrnMut.isPending}>Enregistrer le tiers</Button>
                </div>
              </div>
            )}
          </FormField>
          <FormField label="Bon de commande lié (optionnel)">
            <select {...register('bc_id')} className={selectCls} onChange={(e) => { const bc = bcs?.items.find((b) => b.id === e.target.value); if (bc) { setValue('montant_ht', bc.montant_ht); if (bc.fournisseur_id) setValue('fournisseur_id', bc.fournisseur_id); if (bc.chantier_id) setValue('chantier_id', bc.chantier_id); } }}>
              <option value="">— Aucun —</option>
              {bcs?.items.filter((b) => b.reference).map((b) => <option key={b.id} value={b.id}>{b.reference} · {formatDZD(b.montant_ttc)}</option>)}
            </select>
          </FormField>
          <FormField label="Chantier (optionnel)">
            <select {...register('chantier_id')} className={selectCls}>
              <option value="">— Frais généraux (hors chantier) —</option>
              {(chantiersData?.items ?? []).map((c) => <option key={c.id} value={c.id}>{c.code} · {c.nom}</option>)}
            </select>
          </FormField>
          <FormField label="Date facture" required><Input type="date" {...register('date_facture')} /></FormField>
          <FormField label="Échéance"><Input type="date" {...register('date_echeance')} /></FormField>
          <FormField label="Montant HT (DZD)" required><Input type="number" step="1" {...register('montant_ht')} /></FormField>
          <FormField label="Taux TVA">
            <select {...register('tva_taux')} className={selectCls}>
              {TVA_TAUX_FACTURE.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </FormField>
          <FormField label="Montant TVA (modifiable)"><Input type="number" step="0.01" placeholder={`auto : ${formatDZD(tvaAuto)}`} {...register('tva_montant')} /></FormField>
          <FormField label="Montant TTC (modifiable)"><Input type="number" step="0.01" placeholder={`auto : ${formatDZD(ttcAuto)}`} {...register('montant_ttc')} /></FormField>
          <FormField label="Notes" className="sm:col-span-2"><Input {...register('notes')} /></FormField>
          <label className="col-span-2 flex cursor-pointer items-center gap-2 text-sm text-content-secondary">
            <input type="checkbox" className="size-4 accent-brand-primary" {...register('comptabilisee')} />
            Comptabilisée (traitée par le comptable)
          </label>
          <div className="sm:col-span-2">
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,application/pdf" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleScan(f); e.target.value = ''; }} />
            <div className="flex items-center justify-between">
              <p className="label-caps">Pièces jointes — scan facture, photo, justificatif de paiement</p>
              <Button type="button" variant="secondary" size="sm" onClick={() => fileRef.current?.click()}><Paperclip className="size-4" /> Ajouter</Button>
            </div>
            {pieces.length === 0 ? (
              <p className="mt-1 inline-flex items-center gap-1 text-xs text-status-stuck"><AlertTriangle className="size-3" /> Aucune pièce — facture non justifiée.</p>
            ) : (
              <ul className="mt-2 space-y-1">
                {pieces.map((p, i) => (
                  <li key={i} className="flex items-center justify-between rounded-md border border-status-done/40 bg-status-done/10 px-3 py-1.5 text-sm">
                    <span className="flex items-center gap-2 truncate text-content-primary"><Paperclip className="size-4 shrink-0" /> <span className="truncate">{p.nom ?? p.url}</span></span>
                    <button type="button" onClick={() => setPieces((ps) => ps.filter((_, j) => j !== i))} className="rounded p-1 text-content-muted hover:text-status-stuck" aria-label="Retirer"><Trash2 className="size-4" /></button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </Modal>

      <RapprochementModal facture={rappro} onClose={() => setRappro(null)} />
      <PaiementsModal facture={paiementsFor} onClose={() => setPaiementsFor(null)} onDone={invalidate} />
      <FactureDetailModal
        open={!!detail}
        onClose={() => setDetail(null)}
        facture={detail}
        tiersNom={detail ? (tiersMap.get(detail.fournisseur_id ?? '') ?? null) : null}
        chantierNom={(() => { if (!detail?.chantier_id) return null; const c = chantiersData?.items.find((x) => x.id === detail.chantier_id); return c ? `${c.code} · ${c.nom}` : null; })()}
        onPrint={() => { if (detail) openFacturePdf(detail.id, 'open').catch((e) => toast.error(getApiErrorMessage(e))); }}
        onAddPaiement={() => { const d = detail; setDetail(null); if (d) setPaiementsFor(d); }}
      />
    </div>
  );
}

interface PaiementForm { date_paiement: string; montant_paye: number | ''; periode_mois: number | ''; periode_annee: number | ''; mode_paiement: string; paiement_reference: string; paiement_banque: string; paiement_echeance: string; }

const _aujourdhui = () => new Date().toISOString().slice(0, 10);

function PaiementsModal({ facture, onClose, onDone }: { facture: FactureDTO | null; onClose: () => void; onDone: () => void }) {
  const [f, setF] = useState<FactureDTO | null>(facture);
  useEffect(() => setF(facture), [facture]);
  const { register, handleSubmit, reset, watch } = useForm<PaiementForm>({ defaultValues: { mode_paiement: '' } });
  const modeW = watch('mode_paiement');
  const [editId, setEditId] = useState<string | null>(null);
  // Saisie : 'choix' (deux options), 'solder' (montant figé = reste), 'partiel' (montant libre ≤ reste).
  const [mode, setMode] = useState<'choix' | 'solder' | 'partiel'>('choix');
  useEffect(() => { setMode('choix'); setEditId(null); }, [facture]);
  const reste = Math.max(0, Number(f?.reste_a_payer ?? 0));
  const ouvrirSaisie = (m: 'solder' | 'partiel') => {
    setEditId(null); setMode(m);
    reset({ date_paiement: _aujourdhui(), montant_paye: reste, mode_paiement: '', periode_mois: '', periode_annee: '', paiement_reference: '', paiement_banque: '', paiement_echeance: '' });
  };
  const startEdit = (p: PaiementDTO) => {
    setEditId(p.id); setMode('partiel');
    reset({ date_paiement: p.date_paiement, montant_paye: p.montant_paye, periode_mois: p.periode_mois, periode_annee: p.periode_annee, mode_paiement: p.mode_paiement ?? '', paiement_reference: p.paiement_reference ?? '', paiement_banque: p.paiement_banque ?? '', paiement_echeance: p.paiement_echeance ?? '' });
  };
  const montantW = Number(watch('montant_paye')) || 0;
  const depasse = !editId && montantW > reste + 0.01;  // garde-fou UI (le serveur bloque aussi)
  const { ht: htAuto, tva: tvaAuto } = quotePartHtTva(montantW, f?.tva_pct ?? 0, f?.tva_exonere);

  const recompute = (fc: FactureDTO, paiements: PaiementDTO[]): FactureDTO => {
    const total_paye = paiements.reduce((s, p) => s + p.montant_paye, 0);
    const sp: StatutPaiement = total_paye === 0 ? 'NON_PAYEE' : Math.abs(fc.montant_ttc - total_paye) <= 1 ? 'SOLDEE' : 'PARTIELLE';
    return { ...fc, paiements, total_paye, reste_a_payer: fc.montant_ttc - total_paye, statut_paiement: sp };
  };
  const saveMut = useMutation({
    mutationFn: (v: PaiementForm) => {
      const payload = {
        date_paiement: v.date_paiement, montant_paye: Number(v.montant_paye) || 0,
        periode_mois: v.periode_mois === '' ? null : Number(v.periode_mois),
        periode_annee: v.periode_annee === '' ? null : Number(v.periode_annee),
        mode_paiement: (v.mode_paiement || null) as ModePaiementFacture | null,
        paiement_reference: v.paiement_reference || null, paiement_banque: v.paiement_banque || null,
        paiement_echeance: v.paiement_echeance || null,
      };
      return editId ? updatePaiement(editId, payload) : addPaiement(f!.id, payload);
    },
    onSuccess: (upd) => { setF(upd); onDone(); reset({ mode_paiement: '' }); setEditId(null); setMode('choix'); toast.success(editId ? 'Règlement modifié.' : 'Règlement enregistré.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const delMut = useMutation({
    mutationFn: (id: string) => deletePaiement(id),
    onSuccess: (_d, id) => { setF((prev) => (prev ? recompute(prev, prev.paiements.filter((p) => p.id !== id)) : prev)); onDone(); toast.success('Paiement supprimé.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  return (
    <Modal open={!!facture} onOpenChange={(o) => { if (!o) onClose(); }} title={`Paiements — facture ${f?.numero_facture ?? ''}`}
      footer={<Button variant="secondary" onClick={onClose}>Fermer</Button>}>
      {f && (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-2">
            <div className="rounded-md bg-surface-hover px-3 py-2"><p className="text-xs text-content-muted">TTC</p><p className="font-bold tabular-nums">{formatDZD(f.montant_ttc)}</p></div>
            <div className="rounded-md bg-surface-hover px-3 py-2"><p className="text-xs text-content-muted">Payé</p><p className="font-bold tabular-nums text-status-done">{formatDZD(f.total_paye)}</p></div>
            <div className="rounded-md bg-surface-hover px-3 py-2"><p className="text-xs text-content-muted">Reste</p><p className="font-bold tabular-nums text-status-stuck">{formatDZD(f.reste_a_payer)}</p></div>
          </div>
          {f.paiements.length > 0 && (
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border text-left text-xs text-content-muted"><th className="py-1">Date</th><th>G50</th><th>Montant</th><th>HT</th><th>TVA</th><th>Mode</th><th /></tr></thead>
              <tbody>
                {f.paiements.map((p) => (
                  <tr key={p.id} className="border-b border-border last:border-0">
                    <td className="py-1.5">{p.date_paiement}</td>
                    <td className="text-xs tabular-nums text-content-muted">{String(p.periode_mois).padStart(2, '0')}/{p.periode_annee}</td>
                    <td className="tabular-nums font-medium">{formatDZD(p.montant_paye)}</td>
                    <td className="tabular-nums text-content-muted">{formatDZD(p.ht_paiement)}</td>
                    <td className="tabular-nums text-content-muted">{formatDZD(p.tva_paiement)}</td>
                    <td className="text-xs">{p.mode_paiement ? MODE_PAIEMENT_FACTURE_LABEL[p.mode_paiement] : '—'}{p.paiement_reference ? ` · ${p.paiement_reference}` : ''}</td>
                    <td className="text-right"><div className="flex justify-end gap-1"><button type="button" onClick={() => startEdit(p)} className="rounded p-1 text-content-muted hover:text-content-primary" aria-label="Modifier"><Pencil className="size-4" /></button><button type="button" onClick={() => { if (window.confirm('Supprimer ce règlement ?')) delMut.mutate(p.id); }} className="rounded p-1 text-content-muted hover:text-status-stuck" aria-label="Supprimer"><Trash2 className="size-4" /></button></div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {/* Payer la facture : deux options claires (Solder / Partiel) */}
          {f.statut_paiement === 'SOLDEE' && !editId ? (
            <div className="rounded-md bg-status-done/10 px-4 py-3 text-center text-sm font-semibold text-status-done">Facture soldée — aucun reste à payer.</div>
          ) : mode === 'choix' && !editId ? (
            <div className="rounded-md border border-border bg-surface-primary p-4">
              <p className="mb-3 text-sm font-semibold text-content-primary">Payer la facture — reste à payer <span className="tabular-nums text-status-stuck">{formatDZD(reste)}</span></p>
              <div className="flex flex-wrap gap-2">
                <Button onClick={() => ouvrirSaisie('solder')}><CheckCircle2 className="size-4" /> Solder ({formatDZD(reste)})</Button>
                <Button variant="secondary" onClick={() => ouvrirSaisie('partiel')}><CreditCard className="size-4" /> Paiement partiel</Button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit((v) => saveMut.mutate(v))} className="rounded-md border border-border bg-surface-primary p-3">
              <p className="mb-2 text-xs font-semibold text-content-secondary">{editId ? 'Modifier le règlement' : mode === 'solder' ? `Solder la facture (${formatDZD(reste)})` : 'Paiement partiel'} — la <strong>période G50</strong> rattache la quote-part de TVA</p>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                <FormField label="Date du paiement" required><Input type="date" {...register('date_paiement')} /></FormField>
                <FormField label="Montant (TTC)" required><Input type="number" step="0.01" max={editId ? undefined : reste} readOnly={mode === 'solder' && !editId} {...register('montant_paye')} /></FormField>
                <FormField label="Mode"><select {...register('mode_paiement')} className={selectCls}><option value="">—</option>{MODES_PAIEMENT_FACTURE.filter((m) => m !== 'NON_PAYE').map((m) => <option key={m} value={m}>{MODE_PAIEMENT_FACTURE_LABEL[m]}</option>)}</select></FormField>
                <FormField label="G50 — mois" hint="défaut = mois du paiement"><Input type="number" min="1" max="12" step="1" placeholder="auto" {...register('periode_mois')} /></FormField>
                <FormField label="G50 — année" hint="modifiable (retard de pièce)"><Input type="number" step="1" placeholder="auto" {...register('periode_annee')} /></FormField>
                {(modeW === 'CHEQUE' || modeW === 'VIREMENT' || modeW === 'EFFET') && <FormField label={modeW === 'EFFET' ? "N° de l'effet" : modeW === 'CHEQUE' ? 'N° de chèque' : 'Réf. virement'}><Input {...register('paiement_reference')} /></FormField>}
                {(modeW === 'CHEQUE' || modeW === 'VIREMENT') && <FormField label="Banque"><Input {...register('paiement_banque')} /></FormField>}
                {modeW === 'EFFET' && <FormField label="Échéance"><Input type="date" {...register('paiement_echeance')} /></FormField>}
              </div>
              {depasse && <p className="mt-2 text-xs font-semibold text-status-stuck">Le montant dépasse le reste à payer. Maximum autorisé : {formatDZD(reste)}.</p>}
              <p className="mt-2 text-xs text-content-muted">Quote-part : HT <strong>{formatDZD(htAuto)}</strong> · TVA <strong>{formatDZD(tvaAuto)}</strong> (datée du paiement → G50).</p>
              <div className="mt-2 flex justify-end gap-2"><Button type="button" variant="secondary" size="sm" onClick={() => { setEditId(null); setMode('choix'); reset({ mode_paiement: '' }); }}>Annuler</Button><Button type="submit" size="sm" loading={saveMut.isPending} disabled={depasse || montantW <= 0}>{editId ? 'Enregistrer' : mode === 'solder' ? 'Confirmer le solde' : 'Enregistrer le paiement'}</Button></div>
            </form>
          )}
        </div>
      )}
    </Modal>
  );
}

function RapprochementModal({ facture, onClose }: { facture: FactureDTO | null; onClose: () => void }) {
  const { data } = useQuery({ queryKey: ['rapprochement', facture?.id], queryFn: () => getRapprochement(facture!.id), enabled: !!facture });
  const r: RapprochementDTO | undefined = data;
  return (
    <Modal open={!!facture} onOpenChange={(o) => { if (!o) onClose(); }} title={`Rapprochement 3 voies — ${facture?.reference ?? facture?.numero_facture ?? ''}`}
      footer={<Button variant="secondary" onClick={onClose}>Fermer</Button>}>
      {!r ? <p className="py-8 text-center text-sm text-content-muted">Calcul…</p> : (
        <div className="space-y-4">
          <div className={cn('flex items-center justify-between rounded-md px-4 py-3 text-sm font-semibold', r.conforme ? 'bg-status-done/10 text-status-done' : 'bg-status-stuck/10 text-status-stuck')}>
            <span>{r.conforme ? 'Rapprochement conforme' : 'Écart détecté — à vérifier avant paiement'}</span>
            <span>BC {r.bc_reference ?? '—'}</span>
          </div>
          <table className="w-full text-sm">
            <tbody>
              <tr className="border-b border-border"><td className="py-2 text-content-secondary">Commandé (BC)</td><td className="py-2 text-right tabular-nums">{formatDZD(r.commande_ttc)}</td></tr>
              <tr className="border-b border-border"><td className="py-2 text-content-secondary">Reçu (réceptions)</td><td className="py-2 text-right tabular-nums">{formatDZD(r.recu_ttc)}</td></tr>
              <tr className="border-b border-border"><td className="py-2 font-semibold">Facturé</td><td className="py-2 text-right font-semibold tabular-nums text-brand-primary">{formatDZD(r.facture_ttc)}</td></tr>
              <tr className="border-b border-border"><td className="py-2 text-content-secondary">Écart facture − commande</td><td className={cn('py-2 text-right tabular-nums', r.ecart_commande !== 0 && 'text-status-stuck')}>{formatDZD(r.ecart_commande)}</td></tr>
              <tr><td className="py-2 text-content-secondary">Écart facture − reçu</td><td className={cn('py-2 text-right tabular-nums', r.ecart_recu !== 0 && 'text-status-stuck')}>{formatDZD(r.ecart_recu)}</td></tr>
            </tbody>
          </table>
          <p className="text-xs text-content-muted">Quantités : {Number(r.qte_recue)} reçu / {Number(r.qte_commandee)} commandé.</p>
        </div>
      )}
    </Modal>
  );
}
